import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, redirect } from "react-router";
import { VALIDATION_PATTERNS } from "../../constants/app";
import { registerUser } from "../../service/Auth.services";
import { fetchWrapper } from "utils/apiFetch";
import OTPModal from "components/Modals/SignInOtpModal";
import ReCAPTCHA from "react-google-recaptcha";
import APP_CONFIG from "../../constants/app";
import { ROLES } from "../../constants/roles";

const rolesLabelWithIds = Object.values(ROLES).map((val) => ({
  label: val.name,
  value: val.level,
}));

// ── Banner API ──────────────────────────────────────────────────────────────
// Backend: POST {action:"GET"} → { status, data: [{ id, name, photo, type }] }
// `photo` comes back as a Django media-relative path (e.g. "banners/x.jpg"),
// not a full URL — adjust BANNER_MEDIA_BASE below if your media root differs.
const BANNER_ENDPOINT    = "https://pro.mokshiq.in/banner_management";
const BANNER_MEDIA_BASE  = "https://pro.mokshiq.in/media/";
const resolveBannerUrl = (photo) => {
  if (!photo) return "";
  return /^https?:\/\//i.test(photo) ? photo : `${BANNER_MEDIA_BASE}${photo}`;
};

/* ─────────────────────────────────────────────
   Scoped styles — Fintech Animated Theme
   (Matches SignIn.jsx & ForgotPassword.jsx ft-* system)
───────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');

  .ft-page *, .ft-page *::before, .ft-page *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .ft-page {
    min-height: 100vh;
    display: flex;
    font-family: 'Manrope', sans-serif;
    background: #061024;
    overflow: hidden;
    position: relative;
  }

  /* ── Canvas ── */
  .ft-canvas {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }

  /* ── Particles ── */
  .ft-particles {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    overflow: hidden;
  }
  .ft-particle {
    position: absolute;
    border-radius: 50%;
    opacity: 0;
    animation: ft-particle-float linear infinite;
  }
  @keyframes ft-particle-float {
    0%   { opacity: 0;   transform: translateY(100vh) scale(0); }
    10%  { opacity: 0.6; }
    90%  { opacity: 0.3; }
    100% { opacity: 0;   transform: translateY(-10vh) scale(1); }
  }

  /* ── Grid ── */
  .ft-grid {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    background-image:
      linear-gradient(rgba(212,175,55,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(212,175,55,0.04) 1px, transparent 1px);
    background-size: 48px 48px;
  }

  /* ── Orbs ── */
  .ft-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    z-index: 1;
    animation: ft-orb-drift ease-in-out infinite alternate;
  }
  .ft-orb-1 {
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%);
    top: -100px; left: -100px;
    animation-duration: 10s;
  }
  .ft-orb-2 {
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(28,63,148,0.22) 0%, transparent 70%);
    bottom: -80px; right: 300px;
    animation-duration: 14s;
    animation-delay: -3s;
  }
  .ft-orb-3 {
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(212,175,55,0.1) 0%, transparent 70%);
    top: 40%; right: -60px;
    animation-duration: 9s;
    animation-delay: -6s;
  }
  @keyframes ft-orb-drift {
    from { transform: translate(0, 0) scale(1); }
    to   { transform: translate(20px, 30px) scale(1.08); }
  }

  /* ── Ticker ── */
  .ft-ticker-wrap {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 2;
    height: 32px;
    background: rgba(212,175,55,0.05);
    border-top: 1px solid rgba(212,175,55,0.12);
    overflow: hidden;
    display: flex;
    align-items: center;
  }
  .ft-ticker {
    display: flex;
    gap: 0;
    animation: ft-ticker-scroll 28s linear infinite;
    white-space: nowrap;
  }
  @keyframes ft-ticker-scroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  .ft-ticker-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 28px;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: rgba(212,175,55,0.55);
    letter-spacing: 0.04em;
    border-right: 1px solid rgba(212,175,55,0.1);
  }
  .ft-ticker-item .up   { color: #d4af37; }
  .ft-ticker-item .down { color: #f43f5e; }

  /* ── Left panel ── */
  .ft-left {
    flex: 1;
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 60px 48px;
    animation: ft-fade-in 0.8s ease both;
  }
  @keyframes ft-fade-in {
    from { opacity: 0; transform: translateX(-24px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .ft-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 24px;
    animation: ft-drop 0.6s ease both;
  }
  .ft-logo img { max-height: 36px; object-fit: contain; }

  /* ── Banner slider (SKPE navy/gold spotlight) ── */
  .ft-banner-wrap {
    position: relative;
    max-width: 420px;
    margin-bottom: 32px;
    animation: ft-drop 0.6s 0.05s ease both;
  }
  .ft-banner-halo {
    position: absolute;
    inset: -26px;
    z-index: 0;
    background:
      radial-gradient(circle at 15% 20%, rgba(212,175,55,0.32) 0%, transparent 55%),
      radial-gradient(circle at 85% 80%, rgba(28,63,148,0.28) 0%, transparent 55%);
    filter: blur(18px);
    pointer-events: none;
    animation: ft-banner-pulse 6s ease-in-out infinite;
  }
  @keyframes ft-banner-pulse {
    0%, 100% { opacity: 0.7; transform: scale(1); }
    50%      { opacity: 1;   transform: scale(1.05); }
  }
  .ft-banner-slider {
    position: relative;
    z-index: 1;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(120deg, #d4af37, #1c3f94, #d4af37);
    background-size: 220% 220%;
    animation: ft-banner-border 8s linear infinite;
    box-shadow: 0 16px 40px rgba(0,0,0,0.4);
  }
  @keyframes ft-banner-border {
    0%   { background-position: 0% 50%; }
    100% { background-position: 220% 50%; }
  }
  .ft-banner-inner {
    position: relative;
    border-radius: 14px;
    overflow: hidden;
    background: #0a1730;
  }
  .ft-banner-track {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 8.5;
    background: linear-gradient(135deg, #0a1730, #0d1f42);
  }
  .ft-banner-slide {
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 0.7s ease;
  }
  .ft-banner-slide.active { opacity: 1; }
  .ft-banner-slide img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
  }
  .ft-banner-dots {
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 6px;
    z-index: 2;
  }
  .ft-banner-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(255,255,255,0.4);
    border: none;
    cursor: pointer;
    padding: 0;
    transition: all 0.25s;
  }
  .ft-banner-dot.active {
    background: linear-gradient(90deg, #d4af37, #1c3f94);
    width: 18px;
    border-radius: 3px;
  }

  .ft-headline {
    margin-bottom: 20px;
    animation: ft-drop 0.6s 0.1s ease both;
  }
  .ft-headline-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: #d4af37;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 16px;
    padding: 4px 10px;
    border: 1px solid rgba(212,175,55,0.3);
    border-radius: 4px;
    background: rgba(212,175,55,0.06);
  }
  .ft-headline-tag::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #d4af37;
    box-shadow: 0 0 8px #d4af37;
    animation: ft-blink 1.8s ease-in-out infinite;
  }
  @keyframes ft-blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
  .ft-headline h1 {
    font-size: clamp(1.8rem, 3vw, 2.6rem);
    font-weight: 700;
    color: #fff;
    line-height: 1.12;
    letter-spacing: -0.02em;
  }
  .ft-headline h1 .accent { color: #d4af37; }

  .ft-desc {
    font-size: 13px;
    color: rgba(255,255,255,0.4);
    line-height: 1.7;
    max-width: 360px;
    margin-bottom: 36px;
    animation: ft-drop 0.6s 0.2s ease both;
  }

  /* ── Feature list ── */
  .ft-features {
    display: flex;
    flex-direction: column;
    gap: 12px;
    animation: ft-drop 0.6s 0.3s ease both;
    margin-bottom: 36px;
  }
  .ft-feature-item {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .ft-feature-icon {
    width: 32px; height: 32px;
    border-radius: 8px;
    background: rgba(212,175,55,0.08);
    border: 1px solid rgba(212,175,55,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
  }
  .ft-feature-text {
    font-size: 12px;
    color: rgba(255,255,255,0.5);
    line-height: 1.4;
  }
  .ft-feature-text strong {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: rgba(255,255,255,0.8);
    margin-bottom: 1px;
  }

  /* ── Stats ── */
  .ft-stats {
    display: flex;
    gap: 0;
    animation: ft-drop 0.6s 0.4s ease both;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px;
    background: rgba(255,255,255,0.03);
    overflow: hidden;
    max-width: 380px;
  }
  .ft-stat {
    flex: 1;
    padding: 16px 20px;
    border-right: 1px solid rgba(255,255,255,0.07);
  }
  .ft-stat:last-child { border-right: none; }
  .ft-stat-val {
    font-family: 'DM Mono', monospace;
    font-size: 18px;
    font-weight: 500;
    color: #d4af37;
    margin-bottom: 3px;
    display: block;
  }
  .ft-stat-lbl {
    font-size: 9px;
    color: rgba(255,255,255,0.3);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  /* ── Right panel ── */
  .ft-right {
    width: 520px;
    flex-shrink: 0;
    position: relative;
    z-index: 3;
    background: rgba(10,17,30,0.92);
    border-left: 1px solid rgba(255,255,255,0.07);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 44px 56px;
    animation: ft-slide-right 0.6s ease both;
    backdrop-filter: blur(20px);
    overflow-y: auto;
  }
  @keyframes ft-slide-right {
    from { opacity: 0; transform: translateX(30px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .ft-form-inner { width: 100%; margin: auto 0; }

  .ft-form-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 28px;
  }
  .ft-form-brand img { max-height: 32px; object-fit: contain; }

  .ft-form-hd { margin-bottom: 28px; }
  .ft-form-pre {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    color: #d4af37;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ft-form-pre::before {
    content: '';
    display: block;
    width: 24px; height: 1px;
    background: #d4af37;
  }
  .ft-form-title {
    font-size: 24px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 5px;
    letter-spacing: -0.02em;
  }
  .ft-form-sub {
    font-size: 12px;
    color: rgba(255,255,255,0.35);
    font-weight: 400;
    line-height: 1.6;
  }

  /* ── Alert ── */
  .ft-alert {
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 18px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: ft-alert-in 0.3s ease both;
  }
  @keyframes ft-alert-in {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .ft-alert-danger {
    background: rgba(244,63,94,0.08);
    color: #fb7185;
    border: 1px solid rgba(244,63,94,0.25);
  }

  /* ── Row layout ── */
  .ft-row {
    display: flex;
    gap: 14px;
    margin-bottom: 0;
  }
  .ft-row .ft-field {
    flex: 1;
  }

  /* ── Fields ── */
  .ft-field { margin-bottom: 14px; }
  .ft-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255,255,255,0.45);
    margin-bottom: 7px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .ft-input-wrap { position: relative; }
  .ft-input-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
    pointer-events: none;
    color: rgba(255,255,255,0.25);
    transition: color 0.2s;
  }
  .ft-input {
    width: 100%;
    padding: 11px 16px 11px 38px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    font-family: 'Manrope', sans-serif;
    font-size: 13px;
    color: #fff;
    background: rgba(255,255,255,0.04);
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    outline: none;
    caret-color: #d4af37;
  }
  .ft-input::placeholder { color: rgba(255,255,255,0.2); }
  .ft-input:focus {
    border-color: rgba(212,175,55,0.5);
    background: rgba(212,175,55,0.04);
    box-shadow: 0 0 0 3px rgba(212,175,55,0.1);
  }
  .ft-input-error {
    border-color: rgba(244,63,94,0.4) !important;
    box-shadow: 0 0 0 3px rgba(244,63,94,0.08) !important;
  }
  select.ft-input {
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23d4af37' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 14px;
    padding-right: 36px;
    cursor: pointer;
  }
  select.ft-input option {
    background: #0d1a2a;
    color: #fff;
  }
  .ft-error-msg {
    font-size: 11px;
    color: #fb7185;
    font-weight: 500;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    animation: ft-alert-in 0.2s ease both;
  }

  /* ── Captcha wrapper ── */
  .ft-captcha-wrap {
    margin-bottom: 14px;
    filter: invert(0.88) hue-rotate(160deg) saturate(0.6);
    border-radius: 8px;
    overflow: hidden;
  }

  /* ── Terms checkbox ── */
  .ft-options-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 20px;
  }
  .ft-remember {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }
  .ft-remember input[type="checkbox"] {
    width: 15px; height: 15px;
    accent-color: #d4af37;
    cursor: pointer;
  }
  .ft-remember-label {
    font-size: 12px;
    color: rgba(255,255,255,0.4);
    font-weight: 500;
    cursor: pointer;
    user-select: none;
  }
  .ft-terms-link {
    color: #d4af37;
    font-weight: 600;
    text-decoration: none;
    opacity: 0.9;
    transition: opacity 0.15s;
  }
  .ft-terms-link:hover { opacity: 1; }

  /* ── Submit button ── */
  .ft-btn {
    width: 100%;
    padding: 13px 24px;
    border: none;
    border-radius: 10px;
    font-family: 'Manrope', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    background: linear-gradient(135deg, #d4af37, #b8912a);
    color: #0a1220;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    transition: transform 0.15s, box-shadow 0.15s, background 0.2s;
    position: relative;
    overflow: hidden;
  }
  .ft-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  .ft-btn:hover:not(:disabled)::before { transform: translateX(100%); }
  .ft-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #e0bf47, #c49f30);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(212,175,55,0.3);
  }
  .ft-btn:active:not(:disabled) { transform: translateY(0); box-shadow: none; }
  .ft-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Sign in row ── */
  .ft-signup-row {
    text-align: center;
    font-size: 12px;
    color: rgba(255,255,255,0.3);
    font-weight: 500;
  }
  .ft-signup-row a {
    color: #d4af37;
    font-weight: 700;
    text-decoration: none;
    opacity: 0.9;
    transition: opacity 0.15s;
  }
  .ft-signup-row a:hover { opacity: 1; }

  /* ── Secure badge ── */
  .ft-secure-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 20px;
    font-size: 10px;
    color: rgba(255,255,255,0.2);
    letter-spacing: 0.06em;
  }
  .ft-secure-badge span { font-family: 'DM Mono', monospace; }
  .ft-secure-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #d4af37;
    box-shadow: 0 0 6px #d4af37;
    animation: ft-blink 2.5s ease-in-out infinite;
  }

  @keyframes ft-drop {
    from { opacity: 0; transform: translateY(-14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 991px) {
    .ft-left { display: none; }
    .ft-right { width: 100%; border-left: none; }
    .ft-row { flex-direction: column; gap: 0; }
  }
  @media (max-width: 480px) {
    .ft-right { padding: 32px 20px 52px; }
  }
`;

/* ─────────────────────────────────────────────
   Animated canvas background
───────────────────────────────────────────── */
function AnimatedCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let nodes = [];
    const NODE_COUNT = 40;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    const initNodes = () => {
      nodes = Array.from({ length: NODE_COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.1,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      nodes.forEach((n) => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,175,55,${n.opacity * 0.4})`;
        ctx.fill();
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(212,175,55,${0.07 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    resize(); initNodes(); draw();
    window.addEventListener("resize", () => { resize(); initNodes(); });
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="ft-canvas" />;
}

/* ─────────────────────────────────────────────
   Floating Particles
───────────────────────────────────────────── */
const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  size: `${Math.random() * 4 + 2}px`,
  delay: `${Math.random() * 20}s`,
  duration: `${Math.random() * 15 + 12}s`,
  color: i % 3 === 0 ? "rgba(212,175,55,0.55)" : i % 3 === 1 ? "rgba(28,63,148,0.5)" : "rgba(212,175,55,0.3)",
}));

function Particles() {
  return (
    <div className="ft-particles">
      {PARTICLES.map((p) => (
        <div key={p.id} className="ft-particle" style={{ left: p.left, width: p.size, height: p.size, background: p.color, animationDelay: p.delay, animationDuration: p.duration }} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Ticker strip
───────────────────────────────────────────── */
const TICKERS = [
  { sym: "BTC/USD", val: "67,420.50", chg: "+2.34%", up: true },
  { sym: "ETH/USD", val: "3,841.20",  chg: "+1.12%", up: true },
  { sym: "USDT",    val: "1.0002",    chg: "0.00%",  up: true },
  { sym: "BNB/USD", val: "598.30",    chg: "-0.87%", up: false },
  { sym: "SOL/USD", val: "178.45",    chg: "+3.56%", up: true },
  { sym: "XRP/USD", val: "0.6312",    chg: "-1.23%", up: false },
  { sym: "ADA/USD", val: "0.4523",    chg: "+0.94%", up: true },
  { sym: "DOGE",    val: "0.1842",    chg: "-2.11%", up: false },
];

function Ticker() {
  const items = [...TICKERS, ...TICKERS];
  return (
    <div className="ft-ticker-wrap" aria-hidden="true">
      <div className="ft-ticker">
        {items.map((t, i) => (
          <span key={i} className="ft-ticker-item">
            {t.sym}&nbsp;<span>{t.val}</span>&nbsp;<span className={t.up ? "up" : "down"}>{t.chg}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Banner slider — pulls the live banner list from
   the banner_management API (action: "GET") and
   autoplays through whatever comes back.
───────────────────────────────────────────── */
function BannerSlider() {
  const [banners, setBanners] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const fd = new FormData();
        fd.append("action", "GET");
        const res = await fetchWrapper(BANNER_ENDPOINT, { method: "POST", body: fd });
        if (!cancelled && res?.status && Array.isArray(res.data)) {
          setBanners(res.data);
        }
      } catch (err) {
        console.error("Banner fetch error:", err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (banners.length < 2) return;
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % banners.length);
    }, 4500);
    return () => clearInterval(id);
  }, [banners.length]);

  if (banners.length === 0) return null;

  return (
    <div className="ft-banner-wrap">
      <div className="ft-banner-halo" aria-hidden="true" />
      <div className="ft-banner-slider">
        <div className="ft-banner-inner">
          <div className="ft-banner-track">
            {banners.map((b, i) => (
              <div key={b.id ?? i} className={`ft-banner-slide${i === active ? " active" : ""}`}>
                <img src={resolveBannerUrl(b.photo)} alt={b.name || "Banner"} />
              </div>
            ))}
          </div>
          {banners.length > 1 && (
            <div className="ft-banner-dots">
              {banners.map((b, i) => (
                <button
                  key={b.id ?? i}
                  type="button"
                  className={`ft-banner-dot${i === active ? " active" : ""}`}
                  aria-label={`Show banner ${i + 1}`}
                  onClick={() => setActive(i)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpData, setOtpData] = useState({
    phoneNumber: "",
    userToken: "",
  });
  const [captchaToken, setCaptchaToken] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const validateForm = () => {
    const newErrors = {};
    if (!captchaToken) {
      newErrors.acceptTerms = "Please validate the CAPTCHA setting.";
    }
    if (!formData.name) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (formData.name.length > 50) {
      newErrors.name = "Name must be less than 50 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      newErrors.name = "Name can only contain letters and spaces";
    }
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!VALIDATION_PATTERNS.EMAIL.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!VALIDATION_PATTERNS.PHONE.test(formData.phone)) {
      newErrors.phone = "Phone number is invalid";
    }
    if (!formData.role) {
      newErrors.role = "Role is required";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!VALIDATION_PATTERNS.PASSWORD.test(formData.password)) {
      newErrors.password =
        "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    if (!formData.acceptTerms && !newErrors.acceptTerms) {
      newErrors.acceptTerms = "You must accept the terms and conditions";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (name === "password" && errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const _formData = new FormData();
      _formData.append("name", formData.name);
      _formData.append("email", formData.email);
      _formData.append("phone", formData.phone);
      _formData.append("role", formData.role);
      _formData.append("password", formData.password);
      _formData.append("confirm_password", formData.confirmPassword);
      _formData.append("captcha", captchaToken);
      const result = await registerUser(_formData);
      if (result?.status) {
        setOtpData({
          phoneNumber: formData.phone,
          userToken: result.data?.user_token || "",
        });
        navigate("/login", { replace: true });
        // setOtpStep(true);
      } else {
        setErrors({ general: result?.message || "Registration failed" });
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrors({ general: "An unexpected error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSuccess = (result) => {
    console.log("Registration OTP verification successful:", result);
    if (result.data?.token) {
      localStorage.setItem("authToken", result.data.token);
    }
    if (result.data?.user) {
      localStorage.setItem("user", JSON.stringify(result.data.user));
    }
    navigate(from, { replace: true });
  };

  const handleOTPError = (error) => {
    console.error("Registration OTP verification error:", error);
  };

  const handleResendOTP = async () => {
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.phone);
      // implementation omitted as per original code
    } catch (error) {
      console.error("Resend registration OTP error:", error);
      throw error;
    }
  };

  const handleBackToRegister = () => {
    setOtpStep(false);
    setOtpData({ phoneNumber: "", userToken: "" });
    setErrors({});
  };

  if (otpStep) {
    return (
      <OTPModal
        phoneNumber={otpData.phoneNumber}
        userToken={otpData.userToken}
        otpLength={4}
        onSuccess={handleOTPSuccess}
        onError={handleOTPError}
        onResend={handleResendOTP}
        onBack={handleBackToRegister}
        resendWaitMinutes={1}
        maxResendAttempts={3}
        autoSubmitOnComplete={false}
        title="Verify Registration"
        subtitle="Enter the 4-digit code sent to your phone number to complete registration"
      />
    );
  }

  return (
    <>
      <style>{styles}</style>

      <div className="ft-page">
        {/* Animated background */}
        <AnimatedCanvas />
        <Particles />
        <div className="ft-grid" aria-hidden="true" />
        <div className="ft-orb ft-orb-1" aria-hidden="true" />
        <div className="ft-orb ft-orb-2" aria-hidden="true" />
        <div className="ft-orb ft-orb-3" aria-hidden="true" />

        {/* ── Left panel ── */}
        <div className="ft-left">
          <div className="ft-logo">
            <img src="../assets/images/logo/skpe.jpeg" alt="Logo" />
          </div>

          <BannerSlider />

          <div className="ft-headline">
            <div className="ft-headline-tag">New Account</div>
            <h1>
              Join the <span className="accent">future</span>
              <br />of retail
              <br />finance
            </h1>
          </div>

          <p className="ft-desc">
            Start managing recharges, payments, and banking services
            with our comprehensive suite of retailer tools.
          </p>

          <div className="ft-features">
            <div className="ft-feature-item">
              <div className="ft-feature-icon">⚡</div>
              <div className="ft-feature-text">
                <strong>Instant Activation</strong>
                Account live within minutes of signup
              </div>
            </div>
            <div className="ft-feature-item">
              <div className="ft-feature-icon">🔐</div>
              <div className="ft-feature-text">
                <strong>Bank-grade Security</strong>
                OTP + 256-bit encrypted transactions
              </div>
            </div>
            <div className="ft-feature-item">
              <div className="ft-feature-icon">📊</div>
              <div className="ft-feature-text">
                <strong>Real-time Dashboard</strong>
                Track every transaction as it happens
              </div>
            </div>
          </div>

          <div className="ft-stats">
            <div className="ft-stat">
              <span className="ft-stat-val">₹2.4B+</span>
              <span className="ft-stat-lbl">Processed</span>
            </div>
            <div className="ft-stat">
              <span className="ft-stat-val">1.2M+</span>
              <span className="ft-stat-lbl">Retailers</span>
            </div>
            <div className="ft-stat">
              <span className="ft-stat-val">99.9%</span>
              <span className="ft-stat-lbl">Uptime</span>
            </div>
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="ft-right">
          <div className="ft-form-inner">

            <div className="ft-form-brand">
              <img src="../assets/images/logo/skpe.jpeg" alt="Logo" />
            </div>

            <div className="ft-form-hd">
              <p className="ft-form-pre">Retailer Portal</p>
              <h2 className="ft-form-title">Create Account 🚀</h2>
              <p className="ft-form-sub">Get Started For Free Today!</p>
            </div>

            {errors.general && (
              <div className="ft-alert ft-alert-danger">
                <span>⚠️</span>
                {errors.general}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>

              {/* Name */}
              <div className="ft-field">
                <label className="ft-label" htmlFor="name">Full Name</label>
                <div className="ft-input-wrap">
                  <span className="ft-input-icon">👤</span>
                  <input
                    className={`ft-input${errors.name ? " ft-input-error" : ""}`}
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter Your Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                {errors.name && <p className="ft-error-msg"><span>⚠</span>{errors.name}</p>}
              </div>

              {/* Email */}
              <div className="ft-field">
                <label className="ft-label" htmlFor="email">Email Address</label>
                <div className="ft-input-wrap">
                  <span className="ft-input-icon">✉️</span>
                  <input
                    className={`ft-input${errors.email ? " ft-input-error" : ""}`}
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter Your Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                {errors.email && <p className="ft-error-msg"><span>⚠</span>{errors.email}</p>}
              </div>

              {/* Row: Phone & Role */}
              <div className="ft-row">
                <div className="ft-field">
                  <label className="ft-label" htmlFor="phone">Phone Number</label>
                  <div className="ft-input-wrap">
                    <span className="ft-input-icon">📞</span>
                    <input
                      className={`ft-input${errors.phone ? " ft-input-error" : ""}`}
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="10-digit number"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                  {errors.phone && <p className="ft-error-msg"><span>⚠</span>{errors.phone}</p>}
                </div>

                <div className="ft-field">
                  <label className="ft-label" htmlFor="role">Role</label>
                  <div className="ft-input-wrap">
                    <span className="ft-input-icon">👥</span>
                    <select
                      className={`ft-input${errors.role ? " ft-input-error" : ""}`}
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      disabled={loading}
                    >
                      <option value="">Select Role</option>
                      <option value="5">Retailer</option>
                    </select>
                  </div>
                  {errors.role && <p className="ft-error-msg"><span>⚠</span>{errors.role}</p>}
                </div>
              </div>

              {/* Row: Passwords */}
              <div className="ft-row">
                <div className="ft-field">
                  <label className="ft-label" htmlFor="password">Password</label>
                  <div className="ft-input-wrap">
                    <span className="ft-input-icon">🔒</span>
                    <input
                      className={`ft-input${errors.password ? " ft-input-error" : ""}`}
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Create password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                  {errors.password && <p className="ft-error-msg"><span>⚠</span>{errors.password}</p>}
                </div>

                <div className="ft-field">
                  <label className="ft-label" htmlFor="confirmPassword">Confirm Password</label>
                  <div className="ft-input-wrap">
                    <span className="ft-input-icon">🔒</span>
                    <input
                      className={`ft-input${errors.confirmPassword ? " ft-input-error" : ""}`}
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="Repeat password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                  {errors.confirmPassword && <p className="ft-error-msg"><span>⚠</span>{errors.confirmPassword}</p>}
                </div>
              </div>

              {/* Captcha */}
              <div className="ft-captcha-wrap">
                <ReCAPTCHA
                  sitekey="6LfgDP8rAAAAAEsK_03UQxLtsrBLtMd1O_rKmU36"
                  onChange={(token) => setCaptchaToken(token)}
                />
              </div>

              {/* Terms checkbox */}
              <div className="ft-options-row">
                <label className="ft-remember">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span className="ft-remember-label">
                    I accept the{" "}
                    <a
                      href="https://mokshiq.in/terms_of_conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ft-terms-link"
                    >
                      Terms & Conditions
                    </a>
                  </span>
                </label>
                {errors.acceptTerms && <p className="ft-error-msg"><span>⚠</span>{errors.acceptTerms}</p>}
              </div>

              {/* Submit */}
              <button className="ft-btn" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                      style={{ width: "1rem", height: "1rem", borderWidth: "2px" }}
                    />
                    Creating Account…
                  </>
                ) : (
                  <>Sign Up →</>
                )}
              </button>
            </form>

            <div className="ft-signup-row">
              Already Have An Account?{" "}
              <a href="/login">Sign in here</a>
            </div>

            <div className="ft-secure-badge">
              <div className="ft-secure-dot" aria-hidden="true" />
              <span>256-BIT SSL · OTP SECURED · PCI DSS</span>
            </div>

          </div>
        </div>

        {/* Ticker */}
        <Ticker />
      </div>
    </>
  );
};

export default Register;